import { Alert, Button, Grid, Typography } from "@mui/material"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { isNotEmpty } from "../../helpers/StringHelpers"
import { ErrorCode, ErrorType } from "../../services/ntuc/applicationError"
import { saveUserMembershipInfo } from "../../services/ntuc/memberService/membershipSignup"
import { Status } from "../../services/ntuc/Model"
import { CompanyOption, NEW_COMPANY_CODE } from "../../services/ntuc/userService/company"
import getPostalCode from "../../services/ntuc/userService/postalCode"
import { Option } from "../../services/ntuc/userService/staticData"
import { ActionType } from "../../store/signup/SignupReducer"
// import { AddressAndEmploymentDetailMyInfo } from "../../store/signup/SignupState"
import { useSignupStore } from "../../store/signup/UserSignupStore"
import { CancelButton } from "../CancelButton/CancelButton"
import { useDialogErrorHandler, useAlertErrorHandler } from "../../hooks/ErrorHandler"
import { SaveAndContinueButton } from "../SaveAndContinueButton/SaveAndContinueButton"
import { AddressAndEmploymentTabProps } from "../TabProps/TabProps"
import { AddressTitleField } from "./FormFields/AddressTitleField"
import { BuildingNameField } from "./FormFields/BuildingNameField"
import { BusinessNatureField } from "./FormFields/BusinessNatureField"
import { CompanyNameField } from "./FormFields/CompanyNameField"
import { EmploymentTitleField } from "./FormFields/EmploymentTitleField"
import { FloorNumberField } from "./FormFields/FloorNumberField"
import { GetAddressField } from "./FormFields/GetAddressField"
import { HouseNumberField } from "./FormFields/HouseNumberField"
import { JobTitleField } from "./FormFields/JobTitleField"
import { MonthlySalaryField } from "./FormFields/MonthlySalaryField"
import { OccupationalGroupField } from "./FormFields/OccupationalGroupField"
import { PostalCodeField } from "./FormFields/PostalCodeField"
import { StreetNameField } from "./FormFields/StreetNameField"
import { UnitNumberField } from "./FormFields/UnitNumberField"
import { disableFieldCheck } from "../../services/validation/address-employment-form/disableFieldCheck"
import ErrorDialog from "../Dialog/ErrorDialog"
import { getSignupRequest } from "../../helpers/SignupUtils"
import AddressAndEmployementDetailsValidator, { AddressAndEmployementFormValidation, checkJobTitleEligibility, validateBusinessNatureText, validateCompanyNameText, validateFloorNumberText, validateHouseNumberText, validateJobTitleText, validateMonthlySalaryText, validateOccupationalGroupText, validateOtherCompanyNameText, validateStreetNameText, validateUnitNumberText } from "../../services/validation/address-employment-form/AddressAndEmployementDetailsValidator"
import { InputDataExclusion, OccupationGroupOption } from "../../services/ntuc/userService/occupationGroup"
import { StaticDataContext } from "../../views/signup-flow/SignupFormPage/SignupFormPage"

enum DialogButtonAction {
    BACK = `back_to_home`,
    LOGIN = `login`,
    CANCEL = `cancel`,
    DRAFT = `retrieve_draft`,
    CONTINUE = `continue`,
    LOGIN_ANOTHER = `login_another`,
}

const AddressAndEmployment = (props: AddressAndEmploymentTabProps) => {
    const { t } = useTranslation([`signup`, `common`])
    const { changeTab, defaultMonthlyIncomeOption } = props
    const [state, dispatch] = useSignupStore()

    const staticData = useContext(StaticDataContext)

    const disabledFields = state.disabledFields.addressAndEmploymentDetailMyInfo
    const [hideBusinessNature, setHideBusinessNature] = useState(!(state.addressAndEmploymentDetail.companyName.code === NEW_COMPANY_CODE || state.addressAndEmploymentDetail.companyName.businessNature))

    const [inputDataExclusion, setInputDataExclusion] = useState<string>(`[]`)

    const [postalCodeErrorMessage, setPostalCodeErrorMessage] = useState<string | undefined>()
    const [houseNumberErrorMessage, setHouseNumberErrorMessage] = useState<string | undefined>()
    const [streetNameErrorMessage, setStreetNameErrorMessage] = useState<string | undefined>()
    const [floorNumberErrorMessage, setFloorNumberErrorMessage] = useState<string | undefined>()
    const [unitNumberErrorMessage, setUnitNumberErrorMessage] = useState<string | undefined>()
    const [jobTitleErrorMessage, setJobTitleErrorMessage] = useState<string | undefined>()
    const [companyNameErrorMessage, setCompanyNameErrorMessage] = useState<string | undefined>()
    const [occupationalGroupErrorMessage, setOccupationalGroupErrorMessage] = useState<string | undefined>()
    const [monthlySalaryErrorMessage, setMonthlySalaryErrorMessage] = useState<string | undefined>()
    const [businessNatureErrorMessage, setBusinessNatureErrorMessage] = useState<string | undefined>()
    const [isGetAddressCalled, setIsGetAddressCalled] = useState(false)
    const [isBuildNameEmpty, setIsBuildNameEmpty] = useState(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [alert, alertErrorHandler] = useAlertErrorHandler()

    const [dialog, dialogErrorHandler, closeHandler] = useDialogErrorHandler(
        {
            content: ``,
            title: ``,
            display: false,
        },
    )

    const handleDialogClick = (action?: string): void => {
        switch (action) {
            case DialogButtonAction.BACK:
                if (window.myNtucConfig.homeUrl) {
                    window.location.replace(window.myNtucConfig.homeUrl)
                }
                return
            case DialogButtonAction.LOGIN_ANOTHER:
                if (window.myNtucConfig.loginUrl) {
                    window.location.replace(window.myNtucConfig.loginUrl)
                }
                return
            case DialogButtonAction.CANCEL:
                return closeHandler()
            default:
                break
        }
    }

    useEffect(() => {
        if (!state.addressAndEmploymentDetail.companyName.code) {
            setHideBusinessNature(true)
        }
    }, [state.addressAndEmploymentDetail.companyName])

    useEffect(() => {
        // Save input data exclusion when occupationalGroup data change
        const inputDataExclusion = (state.addressAndEmploymentDetail.occupationalGroup as OccupationGroupOption).inputDataExclusion
        setInputDataExclusion(inputDataExclusion || `[]`)

        // Handle clear data if occupation group is inactive
        const occupationalGroupCode = (state.addressAndEmploymentDetail.occupationalGroup as OccupationGroupOption).code
        const currentOccupationalGroupExistsInStaticData = staticData?.occupation_group.find(v => v.code === occupationalGroupCode)
        if (occupationalGroupCode && !currentOccupationalGroupExistsInStaticData) {
            dispatch({
                type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
                value: {
                    ...state.addressAndEmploymentDetail,
                    occupationalGroup: { code: ``, value: `` },
                    companyName: { code: ``, name: `` },
                    monthlySalary: { code: ``, value: `` },
                    jobTitle: ``,
                },
            })
        }

    }, [state.addressAndEmploymentDetail.occupationalGroup.code])

    const handleSaveAndContinueClick = async () => {
        if (isLoading) { return }

        setIsLoading(true)
        AddressAndEmployementDetailsValidator(state)
            .then(() => {
                saveForm()
            })
            .catch(validationResult => {
                displayErrorMessages(validationResult)
                setIsLoading(false)
            })
    }

    const saveForm = () => {
        const request = getSignupRequest(state)

        saveUserMembershipInfo(request, state.membershipId)
            .then(response => {
                if (response?.metadata.status === Status.SUCCESS) {
                    changeTab?.(`bank`)
                } else {
                    alertErrorHandler()
                }
            })
            .catch(error => {
                if (error.response) {
                    alertErrorHandler(error.response.data.content)
                    dialogErrorHandler(error.response.data.content)
                } else if (error.errorCode in ErrorCode) {
                    if (error.error === ErrorType.JobTitleInEligible) {
                        setJobTitleErrorMessage(error.errorDescription)
                    }
                    alertErrorHandler(error)
                    dialogErrorHandler(error)
                }
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const displayErrorMessages = (validationResult: AddressAndEmployementFormValidation) => {
        setPostalCodeErrorMessage(validationResult.postalCodeValidationMessage)
        setHouseNumberErrorMessage(validationResult.houseNumberValidationMessage)
        setFloorNumberErrorMessage(validationResult.floorNumberValidationMessage)
        setUnitNumberErrorMessage(validationResult.unitNumberValidationMessage)
        setJobTitleErrorMessage(validationResult.jobTitleValidationMessage)
        setCompanyNameErrorMessage(validationResult.companyNameValidationMessage)
        setOccupationalGroupErrorMessage(validationResult.occupationalGroupValidationMessage)
        setMonthlySalaryErrorMessage(validationResult.monthlySalaryValidationMessage)
        setBusinessNatureErrorMessage(validationResult.businessNatureValidationMessage)
    }

    const handleNavigateBack = () => {
        if (changeTab) { changeTab(`personal`) }
    }

    const handlePostalCodeChange = (text: string) => {
        dispatch({
            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
            value: {
                ...state.addressAndEmploymentDetail,
                postalCode: text,
            },
        })
    }

    const handleHouseNumberChange = (text: string) => {
        setHouseNumberErrorMessage(validateHouseNumberText(text))
        dispatch({
            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
            value: {
                ...state.addressAndEmploymentDetail,
                houseNumber: text,
            },
        })
    }

    const handleStreetNameChange = (text: string) => {
        setStreetNameErrorMessage(validateStreetNameText(text))
        dispatch({
            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
            value: {
                ...state.addressAndEmploymentDetail,
                streetName: text,
            },
        })
    }

    const handleFloorNameChange = (text: string) => {
        setFloorNumberErrorMessage(validateFloorNumberText(text))
        dispatch({
            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
            value: {
                ...state.addressAndEmploymentDetail,
                floorNumber: text,
            },
        })
    }

    const handleUnitNameChange = (text: string) => {
        setUnitNumberErrorMessage(validateUnitNumberText(text))
        dispatch({
            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
            value: {
                ...state.addressAndEmploymentDetail,
                unitNumber: text,
            },
        })
    }

    const handleBuildingNameChange = (text: string) => {
        dispatch({
            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
            value: {
                ...state.addressAndEmploymentDetail,
                buildingName: text,
            },
        })
    }

    const handleJobTitleChange = (text: string) => {
        setJobTitleErrorMessage(validateJobTitleText(text))
        dispatch({
            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
            value: {
                ...state.addressAndEmploymentDetail,
                jobTitle: text,
            },
        })
    }

    const handleJobTitleBlur = async (text: string) => {
        const validationMessage = validateJobTitleText(text)
        if (validationMessage) {
            setJobTitleErrorMessage(validationMessage)
        } else {
            const jobTitleErrorMessage = await checkJobTitleEligibility(state)
            setJobTitleErrorMessage(jobTitleErrorMessage)
        }
    }

    const handleCompanyNameChange = (option: CompanyOption) => {
        switch (option.code) {
            default:
                setHideBusinessNature(true)
                setBusinessNatureErrorMessage(``)
                break
            case NEW_COMPANY_CODE:
                setHideBusinessNature(false)
        }

        setCompanyNameErrorMessage(option.code == NEW_COMPANY_CODE ? validateOtherCompanyNameText(option.name) : validateCompanyNameText(option.name))

        dispatch({
            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
            value: {
                ...state.addressAndEmploymentDetail,
                companyName: {
                    name: option.name,
                    code: option.code,
                },
            },
        })
    }

    const handleInputDataExclusion = (inputField: string): boolean => {
        try {
            const parsedInputDataExclusion: string[] = JSON.parse(inputDataExclusion)
            if (parsedInputDataExclusion.length === 0) {
                throw new Error(`Invalid input data exclusion or empty array`)
            }
            return parsedInputDataExclusion.includes(inputField) ? true : false
        } catch (error) {
            return false
        }
    }

    const handleOccupationalGroupChange = (option: Option) => {
        if (disableFieldCheck.has(option.code)) {
            setOccupationalGroupErrorMessage(validateOccupationalGroupText(option.code))
            dispatch({
                type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
                value: {
                    ...state.addressAndEmploymentDetail,
                    occupationalGroup: option,
                    companyName: { code: ``, name: `` },
                    monthlySalary: defaultMonthlyIncomeOption,
                    jobTitle: ``,
                },
            })
            setHideBusinessNature(true)
            setMonthlySalaryErrorMessage(undefined)
            setCompanyNameErrorMessage(undefined)
            setJobTitleErrorMessage(undefined)
            setBusinessNatureErrorMessage(undefined)
        } else {
            setOccupationalGroupErrorMessage(validateOccupationalGroupText(option.code))
            if (disableFieldCheck.has(state.addressAndEmploymentDetail.occupationalGroup.code)) {
                dispatch({
                    type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
                    value: {
                        ...state.addressAndEmploymentDetail,
                        occupationalGroup: option,
                        companyName: state.myInfo.addressAndEmploymentDetail.companyName ?? { code: ``, name: `` },
                        monthlySalary: state.myInfo.addressAndEmploymentDetail.monthlySalary ?? { code: ``, value: `` },
                        jobTitle: state.myInfo.addressAndEmploymentDetail.jobTitle ?? ``,
                    },
                })
            } else {
                dispatch({
                    type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
                    value: {
                        ...state.addressAndEmploymentDetail,
                        occupationalGroup: option,
                        companyName: { code: ``, name: `` },
                        monthlySalary: { code: ``, value: `` },
                        jobTitle: ``,
                    },
                })
            }
        }
    }

    const handleMonthlySalaryChange = (option: Option) => {
        setMonthlySalaryErrorMessage(validateMonthlySalaryText(option.value))
        dispatch({
            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
            value: {
                ...state.addressAndEmploymentDetail,
                monthlySalary: option,
            },
        })
    }
    const handleBusinessNatureChange = (text: string) => {
        setBusinessNatureErrorMessage(validateBusinessNatureText(text))
        dispatch({
            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
            value: {
                ...state.addressAndEmploymentDetail,
                companyName: {
                    ...state.addressAndEmploymentDetail.companyName,
                    businessNature: text,
                },
            },
        })
    }

    const handleGetAddressOnClick = () => {
        if (isNotEmpty(state.addressAndEmploymentDetail.postalCode)) {
            getPostalCode(state.addressAndEmploymentDetail.postalCode)
                .then(response => response.data.content)
                .then(response => {
                    if (response.length > 0) {
                        const element = response[0]
                        dispatch({
                            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
                            value: {
                                ...state.addressAndEmploymentDetail,
                                buildingName: element.buildingName,
                                streetName: element.street,
                                houseNumber: element.block,
                                postalCode: element.postalCode,
                            },
                        })
                        setHouseNumberErrorMessage(validateHouseNumberText(element.block))
                        setStreetNameErrorMessage(validateStreetNameText(element.street))
                        setIsGetAddressCalled(true)
                        setIsBuildNameEmpty(element.buildingName == ``)
                    } else {
                        dispatch({
                            type: ActionType.ADD_ADDRESS_AND_EMPLOYMENT_DETAILS,
                            value: {
                                ...state.addressAndEmploymentDetail,
                                buildingName: ``,
                                streetName: ``,
                                houseNumber: ``,
                                postalCode: ``,
                            },
                        })
                        setIsGetAddressCalled(false)
                        setIsBuildNameEmpty(true)
                    }
                })
        }
    }

    return (
        <Grid
            container
            rowSpacing={2}
            columnSpacing={{
                xs: `16px`,
                sm: `24px`,
                md: `24px`,
                lg: `40px`,
            }}>
            <ErrorDialog open={dialog.display} content={t(dialog.content, { ns: `common` })} title={t(dialog.title, { ns: `common` })} onClose={closeHandler}>
                {dialog.primaryButton && <Button onClick={() => handleDialogClick(dialog.primaryButton)} variant="contained">{t(dialog.primaryButton, { ns: `common` })}</Button>}
                {dialog.secondaryButton && <Button onClick={() => handleDialogClick(dialog.secondaryButton)} >{t(dialog.secondaryButton, { ns: `common` })}</Button>}
            </ErrorDialog>
            <Grid item xs={12} sm={12} md={12} lg={12}>
                {alert && alert.display && <Alert severity="error">{t(alert.content, { ns: `common` })}</Alert>}
            </Grid>
            <Grid item xs={12} sm={12}>
                <AddressTitleField />
            </Grid>
            <Grid item md={4} lg={4}>
                <PostalCodeField
                    defaultValue={state.addressAndEmploymentDetail.postalCode}
                    handleChange={handlePostalCodeChange}
                    errorText={postalCodeErrorMessage}
                    isDisabled={disabledFields.postalCode}
                />
            </Grid>
            <Grid item md={4} lg={6} alignSelf={`center`}>
                <GetAddressField
                    onClick={handleGetAddressOnClick}
                    isDisabled={disabledFields.postalCode}
                />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
                <HouseNumberField
                    defaultValue={state.addressAndEmploymentDetail.houseNumber}
                    handleChange={handleHouseNumberChange}
                    errorText={houseNumberErrorMessage}
                    isDisabled={disabledFields.houseNumber}
                />
            </Grid>

            <Grid item xs={12} sm={12} md={6} lg={6}>
                <StreetNameField
                    defaultValue={state.addressAndEmploymentDetail.streetName}
                    handleChange={handleStreetNameChange}
                    errorText={streetNameErrorMessage}
                    isDisabled={disabledFields.streetName || isGetAddressCalled}
                />
            </Grid>
            <Grid container item xs={12} sm={12} md={6} lg={6}>
                <Grid item xs={5.5}>
                    <FloorNumberField
                        defaultValue={state.addressAndEmploymentDetail.floorNumber}
                        handleChange={handleFloorNameChange}
                        errorText={floorNumberErrorMessage}
                        isDisabled={disabledFields.floorNumber}
                    />
                </Grid>
                <Grid item xs={1}>
                    <Typography mt={2} textAlign="center">-</Typography>
                </Grid>
                <Grid item xs={5.5}>
                    <UnitNumberField
                        defaultValue={state.addressAndEmploymentDetail.unitNumber}
                        handleChange={handleUnitNameChange}
                        errorText={unitNumberErrorMessage}
                        isDisabled={disabledFields.unitNumber}
                    />
                </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
                <BuildingNameField
                    defaultValue={state.addressAndEmploymentDetail.buildingName}
                    handleChange={handleBuildingNameChange}
                    isDisabled={disabledFields.buildingName || (isGetAddressCalled && !isBuildNameEmpty)}
                />
            </Grid>
            <Grid item xs={12} sm={12}>
                <EmploymentTitleField />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
                <OccupationalGroupField
                    defaultValue={state.addressAndEmploymentDetail.occupationalGroup}
                    handleChange={handleOccupationalGroupChange}
                    errorText={occupationalGroupErrorMessage} />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} hidden={handleInputDataExclusion(InputDataExclusion.JobTitle)}>
                <JobTitleField
                    defaultValue={state.addressAndEmploymentDetail.jobTitle}
                    handleChange={handleJobTitleChange}
                    dependentField={state.addressAndEmploymentDetail.occupationalGroup.code}
                    handleBlur={handleJobTitleBlur}
                    errorText={jobTitleErrorMessage}
                    isDisabled={false}
                />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} hidden={handleInputDataExclusion(InputDataExclusion.Company)}>
                <CompanyNameField
                    defaultValue={state.addressAndEmploymentDetail.companyName}
                    handleChange={handleCompanyNameChange}
                    errorText={companyNameErrorMessage}
                    dependentField={state.addressAndEmploymentDetail.occupationalGroup.code}
                    isDisabled={false}
                />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} hidden={hideBusinessNature}>
                <BusinessNatureField
                    defaultValue={state.addressAndEmploymentDetail.companyName.businessNature}
                    handleChange={handleBusinessNatureChange}
                    errorText={businessNatureErrorMessage}
                />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} hidden={handleInputDataExclusion(InputDataExclusion.MonthlyGrossSalary)}>
                <MonthlySalaryField
                    defaultValue={state.addressAndEmploymentDetail.monthlySalary}
                    handleChange={handleMonthlySalaryChange}
                    errorText={monthlySalaryErrorMessage}
                    dependentField={state.addressAndEmploymentDetail.occupationalGroup.code}
                />
            </Grid>
            <Grid
                container
                padding={3}
                rowSpacing={1}
                columnSpacing={{
                    xs: `24px`,
                    sm: `24px`,
                    md: `24px`,
                    lg: `40px`,
                }}>
                <Grid item xs={12} sm={12} md={6} lg={6} textAlign='center'>
                    <CancelButton
                        onClick={handleNavigateBack} />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} textAlign='center'>
                    <SaveAndContinueButton
                        loading={isLoading}
                        onClick={handleSaveAndContinueClick}
                    />
                </Grid>
            </Grid>
        </Grid>
    )
}

export { AddressAndEmployment }
